// ChatService.js
import socketService from './SocketService';
import { getLoggedInUser } from 'utilities/helpers';
const ChatService = {
  /**
   * Initialize chat functionality.
   * Returns a promise that resolves on successful connection.
   */
  initialize(apiUrl) {
    apiUrl =
      apiUrl ||
      process.env.REACT_APP_API_URL2 ||
      'https://api.samskrutimatrimony.com/';
    if (!socketService.socket) {
      socketService.initialize(apiUrl);
    }
    if (!socketService.isConnected) {
      socketService.connect();
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 10000);
      const statusListener = (isConnected, error) => {
        if (isConnected) {
          clearTimeout(timeout);
          socketService.removeConnectionStatusListener(statusListener);
          // Register user after successful connection
          const userId = this.getCurrentUserId();
          if (userId) {
            this.registerUser(userId);
          }
          resolve();
        } else if (error) {
          clearTimeout(timeout);
          socketService.removeConnectionStatusListener(statusListener);
          reject(error);
        }
      };
      socketService.addConnectionStatusListener(statusListener);
      // If already connected, resolve immediately
      if (socketService.isConnected) {
        clearTimeout(timeout);
        socketService.removeConnectionStatusListener(statusListener);
        // Register user if already connected
        const userId = this.getCurrentUserId();
        if (userId) {
          this.registerUser(userId);
        }
        resolve();
      }
    });
  },
  /**
   * Register the current user with the socket server.
   */
  registerUser(userId) {
    if (!socketService.socket || !socketService.isConnected) {
      console.error('Cannot register user: Socket not connected');
      return false;
    }
    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform
    };
    socketService.socket.emit('registerUser', {
      userId,
      deviceInfo
    });
    return true;
  },
  /**
   * Get the current logged-in user's ID.
   */
  getCurrentUserId() {
    const user = getLoggedInUser();
    return user?.user_details?.profile_id;
  },
  /**
   * Send a message to another user.
   */
  sendMessage(recipientId, message) {
    const senderId = this.getCurrentUserId();
    if (!senderId) {
      console.error('Cannot send message: User not logged in');
      return false;
    }
    if (!socketService.socket || !socketService.isConnected) {
      console.error('Cannot send message: Socket not connected');
      return false;
    }
    socketService.socket.emit('sendMessage', {
      senderId,
      recipientId,
      message
    });
    return true;
  },
  /**
   * Get conversation history with a specific user.
   */

  // Add these methods to your existing ChatService.js file

  /**
   * Get user online status.
   */
  getUserStatus(userId) {
    if (!socketService.socket || !socketService.isConnected) {
      console.error('Cannot get user status: Socket not connected');
      return false;
    }
    socketService.socket.emit('getUserStatus', { userId });
    return true;
  },

  /**
   * Listen for user status response.
   */
  onUserStatusResponse(callback) {
    if (!socketService.socket) {
      console.error('Cannot add user status listener: Socket not initialized');
      return () => {};
    }
    socketService.socket.on('userStatusResponse', (data) => {
      console.log('User status response received:', data);
      callback(data);
    });
    return () => socketService.socket.off('userStatusResponse', callback);
  },

  /**
   * Listen for user status changes.
   * This event would be triggered when a user's status changes (online/offline).
   */
  onUserStatusChange(callback) {
    if (!socketService.socket) {
      console.error(
        'Cannot add status change listener: Socket not initialized'
      );
      return () => {};
    }
    socketService.socket.on('userStatusChange', (data) => {
      console.log('User status change event received:', data);
      callback(data);
    });
    return () => socketService.socket.off('userStatusChange', callback);
  },

  getConversation(otherUserId, limit = 50, offset = 0) {
    const userId = this.getCurrentUserId();
    if (!userId) {
      console.error('Cannot get conversation: User not logged in');
      return false;
    }
    if (!socketService.socket || !socketService.isConnected) {
      console.error('Cannot get conversation: Socket not connected');
      return false;
    }
    socketService.socket.emit('getConversation', {
      userId,
      otherUserId,
      limit,
      offset
    });
    return true;
  },
  /**
   * Get all conversations for the current user.
   */
  getUserConversations() {
    const userId = this.getCurrentUserId();
    if (!userId) {
      console.error('Cannot get conversations: User not logged in');
      return false;
    }
    if (!socketService.socket || !socketService.isConnected) {
      console.error('Cannot get user conversations: Socket not connected');
      return false;
    }
    socketService.socket.emit('getUserConversations', { userId });
    return true;
  },
  /**
   * Mark a conversation as read.
   */
  markConversationRead(otherUserId) {
    const userId = this.getCurrentUserId();
    if (!userId) {
      console.error('Cannot mark conversation as read: User not logged in');
      return false;
    }
    if (!socketService.socket || !socketService.isConnected) {
      console.error('Cannot mark conversation as read: Socket not connected');
      return false;
    }
    console.log('Marking conversation as read:', userId, otherUserId);
    socketService.socket.emit('markConversationRead', {
      userId,
      otherUserId,
      timestamp: new Date().toISOString() // Add timestamp to help with synchronization
    });

    return true;
  },
  /**
   * Check if the chat service is connected.
   */
  isConnected() {
    return socketService.isConnected;
  },
  // ------------------ Event Listeners ------------------
  /**
   * Listen for new incoming messages.
   */
  onNewMessage(callback) {
    if (!socketService.socket) {
      console.error('Cannot add message listener: Socket not initialized');
      return () => {};
    }
    socketService.socket.on('newMessage', callback);
    return () => socketService.socket.off('newMessage', callback);
  },
  /**
   * Listen for conversation history.
   */
  onConversationHistory(callback) {
    if (!socketService.socket) return () => {};
    socketService.socket.on('conversationHistory', callback);
    return () => socketService.socket.off('conversationHistory', callback);
  },
  /**
   * Listen for user's conversation list.
   */
  onUserConversations(callback) {
    if (!socketService.socket) {
      console.error(
        'Cannot add user conversations listener: Socket not initialized'
      );
      return () => {};
    }
    socketService.socket.on('userConversations', (data) => {
      console.log('Received userConversations:', data);
      callback(data);
    });
    return () => socketService.socket.off('userConversations', callback);
  },
  /**
   * Listen for message sent acknowledgement.
   */
  onMessageSent(callback) {
    if (!socketService.socket) return () => {};
    socketService.socket.on('messageSent', callback);
    return () => socketService.socket.off('messageSent', callback);
  },
  /**
   * Listen for message sending errors.
   */
  onMessageError(callback) {
    if (!socketService.socket) return () => {};
    socketService.socket.on('messageError', callback);
    return () => socketService.socket.off('messageError', callback);
  },
  /**
   * Listen for conversation marked as read.
   */
  onConversationMarkedRead(callback) {
    if (!socketService.socket) return () => {};
    socketService.socket.on('conversationMarkedRead', callback);
    return () => socketService.socket.off('conversationMarkedRead', callback);
  },
  /**
   * Listen for connection status changes.
   */
  onConnectionStatus(callback) {
    // Check if socket exists
    if (!socketService.socket) {
      console.error(
        'Cannot register connection listener: Socket not initialized'
      );
      return () => {};
    }
    // Create direct Socket.IO event handlers
    const connectHandler = () => {
      console.log('Socket connected - firing callback with true');
      callback(true);
    };
    const disconnectHandler = () => {
      console.log('Socket disconnected - firing callback with false');
      callback(false);
    };
    const errorHandler = (error) => {
      console.log('Socket error - firing callback with false and error', error);
      callback(false, error);
    };
    // Register event handlers directly on socket
    socketService.socket.on('connect', connectHandler);
    socketService.socket.on('disconnect', disconnectHandler);
    socketService.socket.on('connect_error', errorHandler);
    // Also handle the initial state
    if (socketService.socket.connected) {
      console.log(
        'Socket already connected - immediately firing callback with true'
      );
      setTimeout(() => callback(true), 0);
    }
    // Return cleanup function
    return () => {
      socketService.socket.off('connect', connectHandler);
      socketService.socket.off('disconnect', disconnectHandler);
      socketService.socket.off('connect_error', errorHandler);
    };
  },
  /**
   * Listen for message read events.
   * This event is triggered when a specific message is read.
   */
  onMessageRead(callback) {
    if (!socketService.socket) {
      console.error('Cannot add message read listener: Socket not initialized');
      return () => {};
    }
    socketService.socket.on('messageRead', (data) => {
      console.log('Message read event received in service:', data);
      callback(data);
    });
    return () => socketService.socket.off('messageRead', callback);
  },

  // Update the markConversationRead function to be more explicit
  // markConversationRead(otherUserId) {
  //   const userId = this.getCurrentUserId();
  //   if (!userId) {
  //     console.error('Cannot mark conversation as read: User not logged in');
  //     return false;
  //   }
  //   if (!socketService.socket || !socketService.isConnected) {
  //     console.error('Cannot mark conversation as read: Socket not connected');
  //     return false;
  //   }
  //   console.log('Marking conversation as read:', userId, otherUserId);
  //   socketService.socket.emit('markConversationRead', {
  //     userId,
  //     otherUserId,
  //     timestamp: new Date().toISOString() // Add timestamp to help with synchronization
  //   });
  //   return true;
  // },
  /**
   * Disconnect the chat service (soft disconnect).
   */
  disconnect() {
    // Do not fully disconnect socket as it may be shared with other services
    console.log('Chat service disconnected');
  }
};
export default ChatService;
